import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaBox,
  FaChevronDown,
  FaChevronUp,
  FaInbox,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaSort,
  FaBuilding,
  FaEye,
  FaTimes
} from 'react-icons/fa';
import { getSuppliers, deleteSupplier } from './supplierService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import { calculateDistance } from '../../utils/distanceUtils';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import Pagination from '../../components/shared/Pagination';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterGroup,
  FilterLabel,
  FilterButton,
  FilterSection
} from '../../components/styled/GridComponents';
import styled from 'styled-components';

// Additional styled components for the expandable section
const ExpandableContent = styled.div`
  max-height: ${props => props.isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.isExpanded ? props.theme.spacing(2) : '0'};
`;

const ProductList = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  gap: ${props => props.theme.spacing(2)};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const SupplierName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const DistanceBadge = styled(StatusBadge)`
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
`;

const FilterSelect = styled.select`
  height: 36px;
  padding: 0 30px 0 10px;
  font-size: ${props => props.theme.typography.fontSize.small};
  border: 1px solid ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.isActive ? props.theme.colors.primary + '14' : props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:hover, &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ClearFiltersButton = styled.button`
  height: 36px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${props => props.theme.typography.fontSize.small};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.error || '#e74c3c'};
    border-color: ${props => props.theme.colors.error || '#e74c3c'};
  }

  svg { font-size: 0.75rem; }
`;

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [expandedSupplierId, setExpandedSupplierId] = useState(null);
  const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'distance', direction: 'asc' });
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSuppliers();
        setSuppliers(data);
      } catch (err) {
        setError('Failed to load suppliers');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      });
    }

    fetchData();
  }, []);

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async () => {
    try {
      await deleteSupplier(supplierToDelete);
      setSuppliers(suppliers.filter(supplier => supplier.id !== supplierToDelete));
    } finally {
      setDeleteModalOpen(false);
      setSupplierToDelete(null);
    }
  };

  // Extract unique categories and products from loaded suppliers
  const uniqueCategories = [...new Map(
    suppliers.flatMap(s => s.categories).map(c => [c.name, c])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  const uniqueProducts = [...new Map(
    suppliers.flatMap(s => s.products).map(p => [p.name, p])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  const hasActiveFilters = categoryFilter !== 'all' || productFilter !== 'all';

  const clearFilters = () => {
    setCategoryFilter('all');
    setProductFilter('all');
    setCurrentPage(1);
  };

  const getFilteredSuppliers = () => {
    let data = [...suppliers];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      data = data.filter(supplier =>
        supplier.name.toLowerCase().includes(query) ||
        supplier.city.toLowerCase().includes(query) ||
        supplier.products.some(product => product.name.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      data = data.filter(supplier =>
        supplier.categories.some(cat => cat.name === categoryFilter)
      );
    }

    // Apply product filter
    if (productFilter !== 'all') {
      data = data.filter(supplier =>
        supplier.products.some(prod => prod.name === productFilter)
      );
    }

    return data;
  };

  const getSuppliersWithDistance = (data) => {
    return data.map(supplier => ({
      ...supplier,
      distance: userLocation.latitude && userLocation.longitude
        ? calculateDistance(userLocation.latitude, userLocation.longitude, supplier.latitude, supplier.longitude)
        : null
    }));
  };

  const getSortedData = (data) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return data.sort((a, b) => {
      switch (sortConfig.field) {
        case 'name':
          return direction * a.name.localeCompare(b.name);
        case 'city':
          return direction * a.city.localeCompare(b.city);
        case 'distance':
          return direction * ((a.distance || Infinity) - (b.distance || Infinity));
        default:
          return 0;
      }
    });
  };

  const paginateData = (data) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return {
      currentItems: data.slice(indexOfFirstItem, indexOfLastItem)
    };
  };

  const getCurrentSuppliers = () => {
    let data = getFilteredSuppliers();
    data = getSuppliersWithDistance(data);
    data = getSortedData(data);
    const totalItems = data.length;
    const currentItems = paginateData(data).currentItems;
    return { currentItems, totalItems };
  };

  const { currentItems: currentSuppliers, totalItems } = getCurrentSuppliers();

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Suppliers Management"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/suppliers/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton
            isActive={sortConfig.field === 'name'}
            onClick={() => handleSort('name')}
          >
            {sortConfig.field === 'name' ?
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />)
              : <FaSort />
            }
            Name
          </FilterButton>
          <FilterButton
            isActive={sortConfig.field === 'city'}
            onClick={() => handleSort('city')}
          >
            {sortConfig.field === 'city' ?
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />)
              : <FaSort />
            }
            City
          </FilterButton>
          <FilterButton
            isActive={sortConfig.field === 'distance'}
            onClick={() => handleSort('distance')}
          >
            {sortConfig.field === 'distance' ?
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />)
              : <FaSort />
            }
            Distance
          </FilterButton>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Filter by:</FilterLabel>
          <FilterSelect
            value={categoryFilter}
            isActive={categoryFilter !== 'all'}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={productFilter}
            isActive={productFilter !== 'all'}
            onChange={(e) => { setProductFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Products</option>
            {uniqueProducts.map(prod => (
              <option key={prod.id} value={prod.name}>{prod.name}</option>
            ))}
          </FilterSelect>
          {hasActiveFilters && (
            <ClearFiltersButton onClick={clearFilters}>
              <FaTimes /> Clear
            </ClearFiltersButton>
          )}
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {loading ? (
          [...Array(6)].map((_, index) => (
            <Card key={index} isLoading>
              <CardContent />
            </Card>
          ))
        ) : currentSuppliers.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No suppliers found. Try adjusting your search or add a new supplier.</p>
          </EmptyState>
        ) : (
          currentSuppliers.map((supplier) => (
            <Card key={supplier.id}>
              <CardHeader>
                <SupplierName>
                  <FaBuilding />
                  {supplier.name}
                </SupplierName>
                {supplier.distance !== null && (
                  <DistanceBadge>
                    {supplier.distance.toFixed(1)} miles
                  </DistanceBadge>
                )}
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <FaMapMarkerAlt />
                  <span>{supplier.city}, {supplier.state}</span>
                </InfoRow>
                
                <InfoRow>
                  <FaPhone />
                  <span>{supplier.phone}</span>
                </InfoRow>
                
                {supplier.email && (
                  <InfoRow>
                    <FaEnvelope />
                    <span>{supplier.email}</span>
                  </InfoRow>
                )}

                {supplier.notes && (
                  <InfoRow>
                    <span style={{ fontSize: '0.85em', color: 'inherit', opacity: 0.75 }}>{supplier.notes}</span>
                  </InfoRow>
                )}

                <ExpandableContent isExpanded={expandedSupplierId === supplier.id}>
                  <ProductList>
                    {supplier.products.map(product => (
                      <ProductItem key={product.id}>
                        <FaBox />
                        <span>{product.name}</span>
                      </ProductItem>
                    ))}
                  </ProductList>
                </ExpandableContent>
              </CardContent>

              <ActionIconsContainer>
                <ActionButton 
                  onClick={() => setExpandedSupplierId(
                    expandedSupplierId === supplier.id ? null : supplier.id
                  )}
                  title={expandedSupplierId === supplier.id ? "Hide Products" : "Show Products"}
                >
                  {expandedSupplierId === supplier.id ? <FaChevronUp /> : <FaChevronDown />}
                </ActionButton>
                <ActionButton 
                  onClick={() => navigate(`/suppliers/detail/${supplier.id}`)}
                  title="View"
                >
                  <FaEye />
                </ActionButton>
                <ActionButton 
                  onClick={() => navigate(`/suppliers/edit/${supplier.id}`)}
                  title="Edit"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton 
                  onClick={() => {
                    setSupplierToDelete(supplier.id);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {!loading && totalItems > 0 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          paginate={(pageNumber) => setCurrentPage(pageNumber)}
          currentPage={currentPage}
          itemsPerPageOptions={[12, 24, 48, 100]}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {deleteModalOpen && (
        <Modal
          message="Are you sure you want to delete this supplier?"
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setSupplierToDelete(null);
          }}
          title="Confirm Delete"
          isOpen={deleteModalOpen}
        />
      )}
    </DashboardLayout>
  );
};

export default SupplierList;